import { useDraggable } from '@dnd-kit/core'

import Tooltip from '@mui/material/Tooltip'

import { GAP_SIZE } from 'components/farm-dashboard/components/DashboardGrid/constants'
import { WidgetContainer } from 'components/farm-dashboard/components/DashboardGrid/styles'
import { renderWidgetContent } from 'components/farm-dashboard/components/DashboardGrid/utils/renderWidgetContent'
import { getWidgetDimensions } from 'components/farm-dashboard/constants/constants'
import { useWidgetPreview } from 'components/farm-dashboard/context/WidgetPreviewContext'
import type { WidgetConfig } from 'components/farm-dashboard/types'
import { WidgetSize } from 'components/farm-dashboard/types'
import { useAppSelector } from 'store/hooks'
import { COLORS } from 'styles/colors'
import { TypographyPrimary12 } from 'ui/typography/TypographyPrimary12'

export const DockDraggableCard: React.FC<{
	widget: WidgetConfig
}> = ({ widget }) => {
	const { widgets, rows } = useAppSelector(s => s.dashboardSlice)
	// Проверяем наличие виджета как в старой структуре (widgets), так и в новой (rows)
	const allWidgets = rows.length > 0 ? rows.flatMap(r => r.widgets) : widgets
	const isDisabled = allWidgets.some(w => w.type === widget.type)

	const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
		id: `dock-${widget.id}`,
		data: { from: 'dock', type: widget.type, widget },
		disabled: isDisabled,
	})

	const { cellSize } = useWidgetPreview()
	const dims = getWidgetDimensions(widget.size)

	const isFullWidth = widget.size === WidgetSize.FULL

	const gridStyle: React.CSSProperties = isFullWidth
		? {
				display: 'grid',
				gridTemplateColumns: '1fr',
				gridAutoRows: `${cellSize}px`,
				gap: `${GAP_SIZE}px`,
				width: '100%',
			}
		: {
				display: 'grid',
				gridTemplateColumns: `repeat(${dims.w}, ${cellSize}px)`,
				gridAutoRows: `${cellSize}px`,
				gap: `${GAP_SIZE}px`,
			}

	return (
		<Tooltip
			title={
				isDisabled ? (
					<TypographyPrimary12> Виджет уже добавлен</TypographyPrimary12>
				) : (
					''
				)
			}
			placement='top'
			arrow
			componentsProps={{
				arrow: { sx: { color: '#fff' } },
				tooltip: {
					sx: {
						backgroundColor: 'white',
						color: COLORS.secondary,
						fontFamily: 'Montserrat',
						fontWeight: 400,
						fontSize: '12px',
						padding: '10px',
						borderRadius: '8px',
						boxShadow: '0px 4px 16px 0px rgba(0, 0, 0, 0.08)',
					},
				},
			}}
		>
			<div
				ref={setNodeRef}
				{...attributes}
				{...(!isDisabled ? listeners : {})}
				style={{
					cursor: isDisabled ? 'default' : 'grab',
					opacity: isDisabled || isDragging ? 0.7 : 1,
					width: isFullWidth ? '100%' : undefined,
				}}
			>
				<div
					style={{
						position: 'relative',
						pointerEvents: isDisabled ? 'none' : 'auto',
						...gridStyle,
					}}
				>
					<WidgetContainer w={dims.w} h={dims.h} customCellSize={cellSize}>
						{renderWidgetContent(widget)}
					</WidgetContainer>
					{isDisabled && (
						<div
							style={{
								position: 'absolute',
								inset: 0,
								backgroundColor: COLORS.iconOpacity,
								borderRadius: 12,
							}}
						/>
					)}
				</div>
			</div>
		</Tooltip>
	)
}
